import { styled } from 'styled-components';
import { Icon, type IconGlyph, SpaceBetween, Typography, tk } from '@sunwebgroup/ui-components';
import { useIntl } from '@/intl';
import type { GroupStatus } from '@/domain/groups/types';

type AllowedColors = 'color_palette_green_500' | 'color_palette_red_500' | 'color_palette_grey_900';
type GroupStatusType = GroupStatus['status'];

const statusConfig: Record<GroupStatusType, { icon: IconGlyph; title: string; text: string; color?: AllowedColors }> = {
  expired: {
    icon: 'IconFaceSad',
    title: 'groups_status_notification_expired_title',
    text: 'groups_status_notification_expired_description',
  },
  success: {
    icon: 'IconCheckmarkCircle',
    title: 'groups_status_notification_success_title',
    text: 'groups_status_notification_success_description',
    color: 'color_palette_green_500',
  },
  full: {
    icon: 'IconFaceSad',
    title: 'groups_status_notification_full_title',
    text: 'groups_status_notification_full_description',
  },
  error: {
    icon: 'IconWarningTriangle',
    title: 'groups_status_notification_error_title',
    text: 'groups_status_notification_error_description',
    color: 'color_palette_red_500',
  },
};

interface StatusMessageProps {
  status: GroupStatusType | undefined;
  groupName: string | undefined;
}

export function StatusNotification({ status, groupName }: StatusMessageProps) {
  const config = statusConfig[status ?? 'error'];
  const intl = useIntl();

  return (
    <Wrapper>
      <Card>
        <SpaceBetween direction="y" alignItems="center">
          <IconWrapper $color={config.color || 'color_palette_grey_900'}>
            <Icon glyph={config.icon} size="xl" />
          </IconWrapper>
          <Typography $variant="body-md-bold">{intl.formatMessage({ id: config.title }, { groupName })}</Typography>
          <Typography $variant="body-sm">{intl.formatMessage({ id: config.text }, { groupName })}</Typography>
        </SpaceBetween>
      </Card>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 54px;
`;

const Card = styled.div`
  padding: ${tk('spacing_lg')};
  max-width: 480px;
  text-align: center;
`;

const IconWrapper = styled.div<{ $color: AllowedColors }>`
  color: ${({ $color }) => tk($color)};
`;
